import { useMemo } from "react";
import { Users2, Building2, DollarSign, TrendingUp, PieChart as PieIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { fmtBRL, fmtInt, fmtPct } from "@/lib/format";
import { planPrice } from "@/lib/plans";

const classifyDoc = (cpf) => {
  const d = String(cpf || "").replace(/\D/g, "");
  if (d.length === 14) return "pj";
  return "pf"; // 11 dígitos ou vazio
};

export default function PfPjInsightsCard({ clientes = [], plans = [] }) {
  const stats = useMemo(() => {
    const acc = {
      pf: { count: 0, receita: 0 },
      pj: { count: 0, receita: 0 },
    };
    for (const c of clientes) {
      const tipo = classifyDoc(c.cpf);
      acc[tipo].count += 1;
      acc[tipo].receita += planPrice(c.plano_escolhido, plans);
    }
    const total = acc.pf.count + acc.pj.count;
    const totalReceita = acc.pf.receita + acc.pj.receita;
    return {
      ...acc,
      total,
      totalReceita,
      pfTicket: acc.pf.count ? acc.pf.receita / acc.pf.count : 0,
      pjTicket: acc.pj.count ? acc.pj.receita / acc.pj.count : 0,
      pctReceitaPJ: totalReceita > 0 ? (acc.pj.receita / totalReceita) * 100 : 0,
      pctReceitaPF: totalReceita > 0 ? (acc.pf.receita / totalReceita) * 100 : 0,
    };
  }, [clientes, plans]);

  const ticketDiff =
    stats.pfTicket > 0 && stats.pjTicket > 0
      ? (stats.pjTicket / stats.pfTicket - 1) * 100
      : null;

  const dominancia =
    stats.pctReceitaPJ >= 60
      ? { label: "Faturamento dominado por PJ", tone: "text-sky-500", advice: "Ótimo momento pra reforçar campanhas B2B — a máquina PJ está aquecida." }
      : stats.pctReceitaPJ >= 30
      ? { label: "Faturamento equilibrado", tone: "text-amber-500", advice: "Boa base híbrida. Vale medir CAC de PJ pra decidir onde escalar." }
      : stats.pctReceitaPJ > 0
      ? { label: "Faturamento concentrado em PF", tone: "text-emerald-500", advice: "PJ ainda representa pouco — considere ações comerciais dirigidas a empresas." }
      : { label: "Sem clientes PJ no período", tone: "text-muted-foreground", advice: "Nenhum cadastro empresarial ainda. Que tal iniciar uma campanha B2B?" };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="pf-pj-insights-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <PieIcon className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold text-lg">PF × PJ · faturamento e ticket</h3>
        </div>
        <Badge variant="outline" className={dominancia.tone}>{dominancia.label}</Badge>
      </div>

      {/* KPIs comparativos */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        {/* PF */}
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="pf-block">
          <div className="flex items-center gap-2 mb-2">
            <Users2 className="w-4 h-4 text-emerald-500" />
            <div className="text-[10px] uppercase tracking-widest text-emerald-500 font-semibold">Pessoa Física</div>
          </div>
          <div className="flex items-baseline gap-2">
            <div className="font-heading text-2xl font-extrabold tabular-nums">{fmtInt(stats.pf.count)}</div>
            <div className="text-xs text-muted-foreground">cliente(s)</div>
          </div>
          <div className="mt-3 space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Receita</span>
              <span className="font-mono tabular-nums font-semibold">{fmtBRL(stats.pf.receita)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Ticket médio</span>
              <span className="font-mono tabular-nums text-emerald-500 font-semibold" data-testid="ticket-pf">
                {fmtBRL(stats.pfTicket)}
              </span>
            </div>
          </div>
        </div>

        {/* PJ */}
        <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-4" data-testid="pj-block">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-4 h-4 text-sky-500" />
            <div className="text-[10px] uppercase tracking-widest text-sky-500 font-semibold">Pessoa Jurídica</div>
          </div>
          <div className="flex items-baseline gap-2">
            <div className="font-heading text-2xl font-extrabold tabular-nums">{fmtInt(stats.pj.count)}</div>
            <div className="text-xs text-muted-foreground">cliente(s)</div>
          </div>
          <div className="mt-3 space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Receita</span>
              <span className="font-mono tabular-nums font-semibold">{fmtBRL(stats.pj.receita)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Ticket médio</span>
              <span className="font-mono tabular-nums text-sky-500 font-semibold" data-testid="ticket-pj">
                {fmtBRL(stats.pjTicket)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Barra de split de faturamento */}
      <div className="rounded-lg border border-border bg-background p-4 space-y-3">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-primary" />
            <span className="font-semibold">Composição do faturamento</span>
          </div>
          <span className="text-xs text-muted-foreground tabular-nums">Total: {fmtBRL(stats.totalReceita)}</span>
        </div>
        {stats.totalReceita > 0 ? (
          <div className="h-6 rounded-full overflow-hidden bg-muted flex" data-testid="split-bar">
            {stats.pctReceitaPF > 0 && (
              <div
                className="bg-emerald-500 h-full grid place-items-center text-[11px] text-white font-semibold tabular-nums"
                style={{ width: `${stats.pctReceitaPF}%` }}
                title={`PF: ${fmtPct(stats.pctReceitaPF)}`}
              >
                {stats.pctReceitaPF >= 8 && `PF ${fmtPct(stats.pctReceitaPF)}`}
              </div>
            )}
            {stats.pctReceitaPJ > 0 && (
              <div
                className="bg-sky-500 h-full grid place-items-center text-[11px] text-white font-semibold tabular-nums"
                style={{ width: `${stats.pctReceitaPJ}%` }}
                title={`PJ: ${fmtPct(stats.pctReceitaPJ)}`}
              >
                {stats.pctReceitaPJ >= 8 && `PJ ${fmtPct(stats.pctReceitaPJ)}`}
              </div>
            )}
          </div>
        ) : (
          <div className="h-6 rounded-full bg-muted grid place-items-center text-[11px] text-muted-foreground">
            Sem faturamento no período
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            <span className="text-muted-foreground">PF ·</span>
            <span className="font-mono tabular-nums font-semibold text-emerald-500">{fmtPct(stats.pctReceitaPF)}</span>
          </div>
          <div className="flex items-center gap-2 justify-end">
            <span className="w-2.5 h-2.5 rounded-full bg-sky-500" />
            <span className="text-muted-foreground">PJ ·</span>
            <span className="font-mono tabular-nums font-semibold text-sky-500" data-testid="pct-receita-pj">
              {fmtPct(stats.pctReceitaPJ)}
            </span>
          </div>
        </div>
      </div>

      {/* Insight */}
      <div className="mt-4 flex items-start gap-2 text-sm">
        <TrendingUp className={`w-4 h-4 shrink-0 mt-0.5 ${dominancia.tone}`} />
        <div>
          <div className="font-semibold">{dominancia.label}</div>
          <div className="text-muted-foreground text-xs mt-0.5">{dominancia.advice}</div>
          {ticketDiff !== null && (
            <div className="text-xs mt-1">
              O ticket médio PJ está{" "}
              <span className={ticketDiff >= 0 ? "text-sky-500 font-semibold" : "text-emerald-500 font-semibold"}>
                {ticketDiff >= 0 ? "+" : ""}{fmtPct(ticketDiff)}
              </span>{" "}
              {ticketDiff >= 0 ? "acima" : "abaixo"} do ticket PF.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
